import { useContext, useEffect, useMemo, useState } from 'react'
import { ReactReduxContext } from 'react-redux'

import requestWithRetry from '../../api/requestWithRetry'
import { getFeatures, isFeaturesAvailable } from '../selectors'

type OrgFeatureSet = Record<string, { enabled?: boolean } | undefined>

const isFeatureEnabledInSet = (features: OrgFeatureSet | null, featureKey: string): boolean => {
  const feature = features?.[featureKey]
  return !!(feature && feature.enabled)
}

export const useOrganizationFeatureEnabled = (
  organizationId: number | undefined,
  featureKey: string,
): { enabled: boolean; loading: boolean } => {
  const reduxContext = useContext(ReactReduxContext)
  const store = reduxContext?.store

  const [reduxFeatures, setReduxFeatures] = useState<OrgFeatureSet | null>(null)
  const [reduxFeaturesReady, setReduxFeaturesReady] = useState(false)

  const [apiFeatures, setApiFeatures] = useState<OrgFeatureSet | null>(null)
  const [apiLoading, setApiLoading] = useState(false)

  useEffect(() => {
    if (!store) {
      setReduxFeaturesReady(false)
      setReduxFeatures(null)
      return
    }

    const syncFromStore = () => {
      const state = store.getState()
      if (isFeaturesAvailable(state)) {
        setReduxFeatures(getFeatures(state))
        setReduxFeaturesReady(true)
      } else {
        setReduxFeaturesReady(false)
        setReduxFeatures(null)
      }
    }

    syncFromStore()
    return store.subscribe(syncFromStore)
  }, [store])

  useEffect(() => {
    if (reduxFeaturesReady || !organizationId) {
      return
    }

    let cancelled = false
    setApiLoading(true)

    requestWithRetry({ method: 'get', url: `/organizations/${organizationId}/features` })
      .then((features: OrgFeatureSet) => {
        if (!cancelled) {
          setApiFeatures(features)
          setApiLoading(false)
        }
      })
      .catch(() => {
        if (!cancelled) {
          setApiFeatures({})
          setApiLoading(false)
        }
      })

    return () => {
      cancelled = true
    }
  }, [organizationId, reduxFeaturesReady])

  return useMemo(() => {
    if (reduxFeaturesReady) {
      return { enabled: isFeatureEnabledInSet(reduxFeatures, featureKey), loading: false }
    }

    if (!organizationId) {
      return { enabled: false, loading: false }
    }

    if (apiLoading) {
      return { enabled: false, loading: true }
    }

    return { enabled: isFeatureEnabledInSet(apiFeatures, featureKey), loading: false }
  }, [reduxFeaturesReady, reduxFeatures, featureKey, organizationId, apiLoading, apiFeatures])
}

export default useOrganizationFeatureEnabled
